import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import './App.css';

const issuerIdParam = new URLSearchParams(window.location.search).get('issuerID');
const issuerID = issuerIdParam ? issuerIdParam.toString() : '';
console.log('Issuer ID:', issuerID);

// Initialize Stripe at top level with direct values
const stripePromise = await loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY,
  { stripeAccount: issuerID }
);

// Direct API URL 
const API_URL = 'https://eu-api.qashio.com';

// Spinner component for loading state
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  }}>
    <div className="spinner-container">
      <div className="spinner" />
    </div>
    <style>
      {`
        .spinner-container {
          position: relative;
          width: 60px;
          height: 60px;
        }
        
        .spinner {
          position: absolute;
          width: 30px;
          height: 30px;
          margin: 10px;
          border: 4px solid rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          border-top: 4px solid white;
          animation: spin 1s linear infinite;
        }
        
        .spinner-container::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 60px;
          height: 60px;
          border-radius: 50%;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
    <div style={{ marginTop: '20px', fontSize:'20px', color: 'white', fontWeight: '500' }}>
      Loading PIN...
    </div>
  </div>
);

function PinView() {
  const location = useLocation();
  const cardId = new URLSearchParams(location.search).get('cardId') || 'ic_1RInG1Q1KeWcvokHnAzAERCj';
  const token = new URLSearchParams(location.search).get('token');
  
  const [nonce, setNonce] = useState('');
  const [ephemeralKey, setEphemeralKey] = useState('');
  const [error, setError] = useState('');
  const [stripeInstance, setStripeInstance] = useState(null);
  const [elementsInstance, setElementsInstance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [operationStarted, setOperationStarted] = useState(false);
  
  // Handle creating nonce and fetching ephemeral key
  const fetchEphemeralKey = async () => {
    console.log('Using Token from URL:', token);
    try {
      if (!token) {
        setError('Token is required');
        setLoading(false);
        return;
      }

      // Get Stripe instance
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to initialize');
      }
      setStripeInstance(stripe);
      
      // Generate nonce for the card
      console.log('Generating nonce for card:', cardId);
      const nonceResult = await stripe.createEphemeralKeyNonce({
        issuingCard: cardId,
      });

      if (!nonceResult || !nonceResult.nonce) {
        console.error('Failed to generate nonce:', nonceResult);
        throw new Error('Failed to generate nonce');
      }
      
      const generatedNonce = nonceResult.nonce;
      setNonce(generatedNonce);
      console.log('Nonce generated successfully:', generatedNonce);
      
      // Make API request with the nonce
      const apiUrlForFetch = `${API_URL}/api/v2/issuers/stripe_card_ephemeral_keys`;
      console.log('Fetching ephemeral key from:', apiUrlForFetch);
      const response = await fetch(apiUrlForFetch, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          nonce: generatedNonce
        })
      });

      const responseText = await response.text();
      console.log('Raw API response:', responseText);
      
      if (!response.ok) {
        console.error(`API error: ${response.status} - ${response.statusText}, Token: ${token}`);
        throw new Error(`Server error: ${response.status} - ${responseText}`);
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Error parsing JSON response:', e);
        throw new Error('Invalid response format from server');
      }
      
      console.log('API Response Data:', data);

      // Use the secret from the response
      if (!data.secret) { 
        console.error('No secret found in response', data);
        throw new Error('Invalid response from server - missing secret');
      }
      
      // Store the ephemeral key
      setEphemeralKey(data.secret);
      setLoading(false);
      setShowPin(true);
    } catch (err) {
      console.error('Error in fetchEphemeralKey:', err);
      setError(`Error: ${err.message}`);
      setLoading(false);
    }
  };

  // Mount PIN element when all dependencies are ready and showPin is true
  useEffect(() => {
    const mountElements = async () => {
      if (stripeInstance && ephemeralKey && token && nonce && showPin) {
        try {
          console.log('Mounting with values:', {
            cardId,
            nonce,
            ephemeralKeyLength: ephemeralKey.length
          });
          
          // Create elements instance only when needed
          const elements = stripeInstance.elements();
          setElementsInstance(elements);

          // Clear any previous element first
          const container = document.getElementById('pin-display');
          if (container) {
            container.innerHTML = '';
          }

          // Create PIN element with explicit error handling
          const pinElement = elements.create('issuingCardPinDisplay', {
            issuingCard: cardId,
            nonce: nonce,
            ephemeralKeySecret: ephemeralKey,
          });

          console.log('PIN element created, mounting now');
          
          // Mount with error handling
          const mountResult = pinElement.mount('#pin-display');
          console.log('Mount result:', mountResult);
          
          // Add event listener for errors
          pinElement.on('change', (event) => {
            console.log('PIN element change event:', event);
            if (event.error) {
              console.error('PIN element error:', event.error);
              setError(`PIN display error: ${event.error.message}`);
            }
          });
        } catch (err) {
          console.error('Error mounting PIN element:', err);
          setError(`Could not display PIN: ${err.message}`);
          setLoading(false);
        }
      } else {
        console.log('Cannot mount PIN element yet. Missing:', {
          hasStripe: !!stripeInstance,
          hasEphemeralKey: !!ephemeralKey,
          hasToken: !!token,
          hasNonce: !!nonce,
          showPin: showPin
        });
      }
    };
    
    mountElements();
  }, [stripeInstance, ephemeralKey, nonce, token, cardId, showPin]);

  // Handle reveal PIN button click
  const handleRevealPin = () => {
    setOperationStarted(true);
    setLoading(true);
    fetchEphemeralKey();
  };

  return (
    <div className="App">
      <header className="App-header">
        <div style={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh"
        }}>
          <h2 style={{ color: 'gray', fontSize: '20px',  fontWeight: 'bold' }}>Card PIN</h2>
          
          {!operationStarted ? (
            <div style={{
              width: "200px",
              padding: "20px",
              borderRadius: "8px",
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column"
            }}>
              <button
                onClick={handleRevealPin}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "rgba(39, 110, 241, 0.9)",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: "pointer",
                  boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.2s ease"
                }}
              >
                Reveal PIN
              </button>
            </div>
          ) : loading ? (
            <div style={{
              width: "200px",
              padding: "20px",
              borderRadius: "8px",
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}>
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div style={{ 
              width: "280px",
              padding: "20px",
              borderRadius: "8px",
              backgroundColor: "rgba(255, 0, 0, 0.08)",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              color: 'red',
              textAlign: 'center',
              border: '1px solid rgba(255, 0, 0, 0.3)',
              margin: '10px'
            }}>
              <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                {error}
              </div>
              <div style={{ marginTop: '10px', fontSize: '14px', color: 'gray' }}>
                Please try again or contact support.
              </div>
            </div>
          ) : (
            <div>
              <div 
                id="pin-display" 
                style={{
                  width: "200px",
                  padding: "15px",
                  border: "1px solid white",
                  color: "red !important",
                  fontSize: "20px",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                  pointerEvents: "none"
                }}
              />
            </div>
          )}
        </div>
      </header>
    </div>
  );
}

export default PinView; 